import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { motion } from "framer-motion";

interface ChatButtonProps {
  onClick: () => void;
}

export const ChatButton = ({ onClick }: ChatButtonProps) => {
  const [isNearFooter, setIsNearFooter] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const footer = document.getElementById("site-footer");
    if (!footer) return;

    observerRef.current = new IntersectionObserver(
      (entries) => entries.forEach((e) => setIsNearFooter(e.isIntersecting)),
      { rootMargin: "0px 0px 160px 0px", threshold: 0 },
    );

    observerRef.current.observe(footer);
    return () => observerRef.current?.disconnect();
  }, []);

  const ease = [0.22, 1, 0.36, 1] as const;

  return (
    <motion.div
      className="fixed z-[200] right-4 md:right-5 pointer-events-auto"
      style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + 16px)` }}
      initial={{ opacity: 0, scale: 0.98, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: isNearFooter ? -72 : 0 }}
      transition={{ type: "tween", duration: 0.28, ease }}
    >
      {/* Stable hit-area wrapper (never moves) */}
      <div className="relative" style={{ padding: 10 }}>
        <motion.button
          onClick={onClick}
          aria-label="Open chat"
          whileHover={{ y: -2, scale: 1.02, ["--p" as any]: 1 }}
          whileTap={{ scale: 0.985, y: -1 }}
          transition={{ type: "tween", duration: 0.22, ease }}
          style={
            {
              width: 56,
              height: 56,
              borderRadius: 22, // softer, more “organic” than a sharp rounded-square
              position: "relative",
              border: "none",
              padding: 0,
              cursor: "pointer",
              outline: "none",
              background: "transparent",
              WebkitTapHighlightColor: "transparent",
              willChange: "transform",
              // IMPORTANT: NO FROST (no blur). Just optical grading.
              backdropFilter: "saturate(1.25) contrast(1.12)",
              ["--p" as any]: 0.6,
            } as CSSProperties
          }
        >
          {/* Deep contact shadow (button sits on page) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              boxShadow: "0 26px 60px rgba(0,0,0,0.62), 0 10px 22px rgba(0,0,0,0.38)",
              opacity: 0.9,
            }}
          />

          {/* Clear glass body (NO gradients, NO highlight bands, NO frost) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              background: "rgba(255,255,255,0.045)", // stays see-through on any background
              border: "1px solid rgba(255,255,255,0.16)",
              boxShadow:
                // thickness + depth (no “top border” highlight)
                "inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -18px 26px rgba(0,0,0,0.28)",
              overflow: "hidden",
            }}
          />

          {/* Edge thickness / refraction feel (subtle, not a highlight stripe) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              // This boosts edges and corners like thick glass without adding a “top band”
              background:
                "radial-gradient(140% 140% at 50% 50%, rgba(255,255,255,0) 52%, rgba(255,255,255,0.12) 74%, rgba(0,0,0,0.22) 100%)",
              opacity: 0.55,
              mixBlendMode: "normal",
            }}
          />

          {/* Single clean rim (prevents double-square look) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
              opacity: 0.65,
            }}
          />

          {/* Pink LED icon glow (classy, controlled) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 22,
              background:
                "radial-gradient(70% 70% at 50% 58%, rgba(255,80,220,0.16) 0%, rgba(255,80,220,0.06) 38%, rgba(255,80,220,0) 66%)",
              opacity: "calc(0.55 + var(--p) * 0.35)",
              mixBlendMode: "screen",
            }}
          />

          {/* Icon */}
          <div className="absolute inset-0 grid place-items-center" style={{ borderRadius: 22 }}>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255, 95, 220, 0.98)"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                filter: "drop-shadow(0 0 10px rgba(255,95,220,0.55)) drop-shadow(0 0 20px rgba(255,95,220,0.28))",
              }}
            >
              <path d="M21 15a4 4 0 0 1-4 4H9l-6 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            </svg>
          </div>
        </motion.button>
      </div>
    </motion.div>
  );
};
